<section class="page-container plans-page">
  <app-page-header
    title="Planes y suscripciones"
    description="Administra los planes disponibles para las empresas."
  >
    <app-button type="button" variant="primary" size="large" (clicked)="nuevoPlan()">
      <i button-icon class="fa-solid fa-plus"></i>
      Nuevo plan
    </app-button>
  </app-page-header>

  <div class="summary-grid" aria-label="Resumen de planes">
    <app-summary-card
      label="Total de planes"
      [value]="totalItems().toString()"
      icon="fa-solid fa-layer-group"
    />
    <app-summary-card
      label="Planes activos"
      [value]="activePlans().toString()"
      icon="fa-solid fa-circle-check"
      tone="green"
    />
    <app-summary-card
      label="Empresas suscritas"
      [value]="subscribedCompanies().toString()"
      icon="fa-solid fa-building"
      tone="neutral"
    />
  </div>

  <section class="data-section-card content-card">
    @if (loadingPage()) {
      <div class="empty-state">
        <app-loading [fullscreen]="true" label="Cargando planes..."></app-loading>
      </div>
    } @else if (loadError()) {
      <div class="empty-state">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <h3>{{ loadError() }}</h3>
        <app-button variant="primary" size="large" type="button" (clicked)="loadPlans()"
          >Reintentar</app-button
        >
      </div>
    } @else {
      @if (loadingTable()) {
        <app-loading [fullscreen]="true"></app-loading>
      }
      <div class="section-title">
        <div>
          <h2>Planes disponibles</h2>
          <span>{{ filteredPlans().length }} resultados</span>
        </div>
      </div>
      <div class="toolbar">
        <app-search-input
          class="wide-search"
          placeholder="Buscar por nombre o código"
          [(value)]="searchTerm"
          (search)="updateSearch($event)"
        />
        <app-select
          [options]="statusOptions"
          [value]="statusFilter()"
          (valueChange)="updateStatus($event)"
        />
      </div>

      <div class="section-title">
        <div>
          <h2>Planes disponibles</h2>
          <span>{{ filteredPlans().length }} resultados</span>
        </div>
      </div>

      @if (filteredPlans().length) {
        <div class="plans-grid">
          @for (plan of filteredPlans(); track plan.code) {
            <article class="plan-card" [class.highlighted]="plan.highlighted">
              @if (plan.highlighted) {
                <span class="recommended">Más utilizado</span>
              }
              <div class="plan-card-header">
                <div>
                  <span class="plan-code">{{ plan.code }}</span>
                  <h3>{{ plan.name }}</h3>
                </div>
                <span class="status" [class.inactive]="plan.status === 'Inactivo'"
                  ><span></span>{{ plan.status }}</span
                >
              </div>
              <p class="plan-description">{{ plan.description }}</p>
              <div class="plan-limit">
                <i class="fa-solid fa-building-columns"></i><strong>{{ plan.accounts }}</strong>
              </div>
              <ul>
                @for (feature of plan.features; track feature) {
                  <li><i class="fa-solid fa-check"></i>{{ feature }}</li>
                }
              </ul>
              <div class="plan-footer">
                <app-button
                  class="link-button"
                  type="button"
                  (clicked)="verEmpresas(plan)"
                  variant="text"
                  size="small"
                >
                  {{ plan.companies }}
                  {{ plan.companies === 1 ? 'empresa suscrita' : 'empresas suscritas' }}
                </app-button>
                <div class="plan-actions">
                  <app-button
                    class="icon-button"
                    type="button"
                    (clicked)="editarPlan(plan)"
                    ariaLabel="Editar plan"
                    variant="text"
                    size="small"
                  >
                    <i button-icon class="fa-solid fa-pen"></i>
                  </app-button>
                  <app-button
                    variant="text"
                    size="small"
                    [danger]="plan.status === 'Activo'"
                    [ariaLabel]="plan.status === 'Activo' ? 'Desactivar plan' : 'Activar plan'"
                    (clicked)="toggleStatus(plan)"
                    [loading]="statusChangingId() === plan.id"
                  >
                    <i
                      button-icon
                      class="fa-solid"
                      [class.fa-toggle-on]="plan.status === 'Activo'"
                      [class.fa-toggle-off]="plan.status !== 'Activo'"
                    ></i>
                  </app-button>
                </div>
              </div>
            </article>
          }
        </div>
      } @else {
        <div class="empty-state">
          <i class="fa-solid fa-layer-group"></i>
          <h3>No se encontraron planes</h3>
          <p>Prueba con otro término de búsqueda o filtro.</p>
        </div>
      }
      <div class="plans-pagination">
        <app-button
          variant="outlined"
          size="small"
          [disabled]="page() === 1"
          (clicked)="previousPage()"
        >
          Anterior
        </app-button>
        <span>Página {{ page() }}</span>
        <app-button
          variant="outlined"
          size="small"
          [disabled]="page() * pageSize() >= totalItems()"
          (clicked)="nextPage()"
        >
          Siguiente
        </app-button>
      </div>
    }
  </section>

  @if (showForm) {
    <app-modal-shell title="" [showHeader]="false" (closed)="closeForm()">
      <app-plan-form
        [plan]="planEditing"
        [serverError]="saveError()"
        (saved)="onPlanSaved($event)"
        (cancelled)="closeForm()"
      />
    </app-modal-shell>
  }

  @if (showEmpresas) {
    <app-modal-shell
      [title]="'Empresas suscritas · ' + (selectedPlan?.name ?? '')"
      description="Empresas asociadas al plan seleccionado."
      size="large"
      (closed)="closeEmpresas()"
    >
      <app-empresas-suscritas
        [planId]="selectedPlan?.id ?? null"
        [planName]="selectedPlan?.name ?? ''"
      />
    </app-modal-shell>
  }

  @if (showDeleteConfirmation) {
    <app-confirmation-modal
      title="Eliminar plan"
      [message]="deleteMessage"
      (confirmed)="confirmarEliminarPlan()"
      (cancelled)="cancelarEliminarPlan()"
      confirmLabel="Eliminar"
      cancelLabel="Cancelar"
      successTitle="Plan eliminado"
      errorTitle="No se pudo eliminar el plan"
      [status]="deleteStatus"
      [processing]="deleting()"
      [autoClose]="deleteStatus === 'success'"
      [iconColor]="colorIcon"
      [iconBackgroundColor]="backgroundIcon"
      (closed)="closeDeleteConfirmation()"
    />
  }
</section>
